import React, { useState } from 'react';
import {
  SchemaEvaluation,
  ExecutiveQuestionFinding,
  ExecutiveQuestionsAnalysis,
  BusinessContextAnalysis,
  TechnicalAnalysis,
  Neo4jSchema
} from '../../types';
import { Card } from '../ui/Card';
import { Badge } from '../ui/Badge';
import Button from '../ui/Button';
import { api } from '../../lib/api';
import { Sparkles, AlertTriangle } from 'lucide-react';

interface SchemaEvaluationResultsProps {
  evaluation: SchemaEvaluation;
  currentSchemaVersion?: number;
  onSchemaRefined?: (refinedSchema: Neo4jSchema) => void;
}

export const SchemaEvaluationResults: React.FC<SchemaEvaluationResultsProps> = ({
  evaluation,
  currentSchemaVersion,
  onSchemaRefined
}) => {
  const [expandedSections, setExpandedSections] = useState<Set<string>>(new Set(['summary']));
  const [isRefining, setIsRefining] = useState(false);
  const [refinementError, setRefinementError] = useState<string | null>(null);
  const [refinementSuccess, setRefinementSuccess] = useState(false);

  // Check if evaluation is outdated
  const isOutdated = currentSchemaVersion !== undefined &&
                     evaluation.schemaVersion !== undefined &&
                     evaluation.schemaVersion < currentSchemaVersion;

  const toggleSection = (section: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(section)) {
      newExpanded.delete(section);
    } else {
      newExpanded.add(section);
    }
    setExpandedSections(newExpanded);
  };

  const getScoreColor = (score: number): string => {
    if (score >= 80) return 'text-green-600 bg-green-50 border-green-200';
    if (score >= 60) return 'text-yellow-600 bg-yellow-50 border-yellow-200';
    return 'text-red-600 bg-red-50 border-red-200';
  };

  const getSupportBadgeColor = (supported: string): string => {
    switch (supported) {
      case 'full':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'partial':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'none':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const getAlignmentBadgeColor = (alignment: string): string => {
    switch (alignment) {
      case 'excellent':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'good':
        return 'bg-blue-100 text-blue-800 border-blue-200';
      case 'fair':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'poor':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  const formatScore = (score: number): string => {
    return `${score}/100`;
  };

  const formatDate = (dateString: string): string => {
    return new Date(dateString).toLocaleString();
  };

  const formatCoverage = (coverage: number | null | undefined): string => {
    // Handle null/undefined
    if (coverage === null || coverage === undefined || isNaN(coverage)) {
      return '0%';
    }

    // If value is between 0 and 1, treat as decimal and convert to percentage
    if (coverage >= 0 && coverage <= 1) {
      return `${Math.round(coverage * 100)}%`;
    }

    // If value is greater than 1, treat as percentage already
    if (coverage > 1 && coverage <= 100) {
      return `${Math.round(coverage)}%`;
    }

    // Fallback for any other case
    return '0%';
  };

  const getCoverageNumber = (coverage: number | null | undefined): number => {
    // Handle null/undefined
    if (coverage === null || coverage === undefined || isNaN(coverage)) {
      return 0;
    }

    // If value is between 0 and 1, treat as decimal and convert to percentage
    if (coverage >= 0 && coverage <= 1) {
      return Math.round(coverage * 100);
    }

    // If value is greater than 1, treat as percentage already
    if (coverage > 1 && coverage <= 100) {
      return Math.round(coverage);
    }

    // Fallback for any other case
    return 0;
  };

  const handleRefineSchema = async () => {
    setIsRefining(true);
    setRefinementError(null);
    setRefinementSuccess(false);

    try {
      // Call the refinement API endpoint
      const response = await api.post<{ schema: Neo4jSchema }>(
        `/schema-design/cases/${evaluation.caseId}/schema/refine`,
        {
          evaluation_version: evaluation.version,
          schema_version: evaluation.schemaId ? parseInt(evaluation.schemaId.split('_').pop() || '1') : undefined
        }
      );

      setRefinementSuccess(true);

      // Call the onSchemaRefined callback if provided
      if (onSchemaRefined && response.schema) {
        onSchemaRefined(response.schema);
      }

      // Show success message for 3 seconds
      setTimeout(() => {
        setRefinementSuccess(false);
      }, 3000);
    } catch (error: any) {
      console.error('Error refining schema:', error);
      setRefinementError(
        error.response?.data?.detail || 'Failed to refine schema. Please try again.'
      );
    } finally {
      setIsRefining(false);
    }
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <div className="flex items-center gap-3">
            <h2 className="text-2xl font-bold text-gray-900">Schema Evaluation Results</h2>
            <Badge className="bg-blue-100 text-blue-800 border-blue-200">
              v{evaluation.version}
            </Badge>
            {evaluation.isActive && (
              <Badge className="bg-green-100 text-green-800 border-green-200">
                ACTIVE
              </Badge>
            )}
          </div>
          <p className="text-sm text-gray-500 mt-1">Evaluated on {formatDate(evaluation.evaluatedAt)}</p>
        </div>
        <div className="flex items-center gap-4">
          <div className={`px-6 py-3 rounded-lg border-2 ${getScoreColor(evaluation.overallScore)}`}>
            <div className="text-sm font-medium">Overall Score</div>
            <div className="text-3xl font-bold">{formatScore(evaluation.overallScore)}</div>
          </div>
          <Button
            variant="primary"
            size="md"
            onClick={handleRefineSchema}
            loading={isRefining}
            disabled={isRefining || refinementSuccess}
            className="whitespace-nowrap"
          >
            <Sparkles className="w-4 h-4" />
            {isRefining ? 'Refining...' : refinementSuccess ? 'Refined!' : 'Refine Schema'}
          </Button>
        </div>
      </div>

      {/* Outdated Evaluation Warning */}
      {isOutdated && (
        <Card className="border-amber-200 bg-amber-50">
          <div className="p-4 flex items-start">
            <div className="flex-shrink-0">
              <AlertTriangle className="w-5 h-5 text-amber-600" />
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-amber-800">Outdated Evaluation</h3>
              <p className="text-sm text-amber-700 mt-1">
                This evaluation is based on schema version <strong>v{evaluation.schemaVersion}</strong>,
                but the current active schema is <strong>v{currentSchemaVersion}</strong>.
                The results may not reflect the latest schema changes.
              </p>
              <button
                onClick={() => {
                  // Trigger re-evaluation by clicking the "Evaluate Schema" button
                  const evaluateButton = document.querySelector('[data-action="evaluate-schema"]') as HTMLButtonElement;
                  if (evaluateButton) evaluateButton.click();
                }}
                className="mt-2 text-sm font-medium text-amber-800 hover:text-amber-900 underline"
              >
                Run evaluation on current schema version
              </button>
            </div>
          </div>
        </Card>
      )}

      {/* Refinement Status Messages */}
      {refinementError && (
        <Card className="border-red-200 bg-red-50">
          <div className="p-4 flex items-start">
            <div className="flex-shrink-0">
              <svg className="w-5 h-5 text-red-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3 flex-1">
              <h3 className="text-sm font-medium text-red-800">Refinement Failed</h3>
              <p className="text-sm text-red-700 mt-1">{refinementError}</p>
            </div>
            <button
              onClick={() => setRefinementError(null)}
              className="ml-3 flex-shrink-0 text-red-600 hover:text-red-800"
            >
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
        </Card>
      )}

      {refinementSuccess && (
        <Card className="border-green-200 bg-green-50">
          <div className="p-4 flex items-start">
            <div className="flex-shrink-0">
              <svg className="w-5 h-5 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
            </div>
            <div className="ml-3">
              <h3 className="text-sm font-medium text-green-800">Schema Refinement Complete</h3>
              <p className="text-sm text-green-700 mt-1">A new refined schema version has been generated with improvements based on the evaluation feedback.</p>
            </div>
          </div>
        </Card>
      )}

      {/* Score Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className={`border-2 ${getScoreColor(evaluation.executiveQuestionsScore)}`}>
          <div className="p-4">
            <div className="text-sm font-medium mb-2">Executive Questions</div>
            <div className="text-2xl font-bold">{formatScore(evaluation.executiveQuestionsScore)}</div>
            <div className="text-xs text-gray-600 mt-1">
              How well schema supports executive business questions
            </div>
          </div>
        </Card>

        <Card className={`border-2 ${getScoreColor(evaluation.businessContextScore)}`}>
          <div className="p-4">
            <div className="text-sm font-medium mb-2">Business Context</div>
            <div className="text-2xl font-bold">{formatScore(evaluation.businessContextScore)}</div>
            <div className="text-xs text-gray-600 mt-1">
              Alignment with business domain requirements
            </div>
          </div>
        </Card>

        <Card className={`border-2 ${getScoreColor(evaluation.technicalQualityScore)}`}>
          <div className="p-4">
            <div className="text-sm font-medium mb-2">Technical Quality</div>
            <div className="text-2xl font-bold">{formatScore(evaluation.technicalQualityScore)}</div>
            <div className="text-xs text-gray-600 mt-1">
              Neo4j best practices and technical quality
            </div>
          </div>
        </Card>
      </div>

      {/* Critical Improvements */}
      {evaluation.criticalImprovements && evaluation.criticalImprovements.length > 0 && (
        <Card className="border-red-200 bg-red-50">
          <div className="p-4">
            <h3 className="text-lg font-semibold text-red-900 mb-3">Critical Improvements Needed</h3>
            <ul className="space-y-2">
              {evaluation.criticalImprovements.map((improvement, index) => (
                <li key={index} className="flex items-start">
                  <span className="inline-block w-6 h-6 rounded-full bg-red-600 text-white text-xs flex items-center justify-center mr-2 mt-0.5 flex-shrink-0">
                    {index + 1}
                  </span>
                  <span className="text-sm text-red-900">{improvement}</span>
                </li>
              ))}
            </ul>
          </div>
        </Card>
      )}

      {/* Executive Questions Analysis */}
      <Card>
        <div
          className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={() => toggleSection('executive')}
        >
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">
              Executive Questions Analysis
            </h3>
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-600">
                {evaluation.executiveQuestionsAnalysis.totalQuestions} questions analyzed
              </div>
              <svg
                className={`w-5 h-5 text-gray-500 transition-transform ${
                  expandedSections.has('executive') ? 'transform rotate-180' : ''
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>

        {expandedSections.has('executive') && (
          <div className="border-t">
            {/* Summary Stats */}
            <div className="p-4 bg-gray-50 border-b">
              <div className="grid grid-cols-3 gap-4">
                <div className="text-center">
                  <div className="text-2xl font-bold text-green-600">
                    {evaluation.executiveQuestionsAnalysis.fullySupported}
                  </div>
                  <div className="text-xs text-gray-600">Fully Supported</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-yellow-600">
                    {evaluation.executiveQuestionsAnalysis.partiallySupported}
                  </div>
                  <div className="text-xs text-gray-600">Partially Supported</div>
                </div>
                <div className="text-center">
                  <div className="text-2xl font-bold text-red-600">
                    {evaluation.executiveQuestionsAnalysis.notSupported}
                  </div>
                  <div className="text-xs text-gray-600">Not Supported</div>
                </div>
              </div>
            </div>

            {/* Detailed Findings */}
            <div className="p-4 space-y-4">
              {evaluation.executiveQuestionsAnalysis.findings.map((finding, index) => (
                <ExecutiveQuestionCard key={index} finding={finding} />
              ))}
            </div>
          </div>
        )}
      </Card>

      {/* Business Context Analysis */}
      <Card>
        <div
          className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={() => toggleSection('business')}
        >
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">
              Business Context Analysis
            </h3>
            <div className="flex items-center space-x-4">
              <Badge className={getAlignmentBadgeColor(evaluation.businessContextAnalysis.domainAlignment || 'fair')}>
                {(evaluation.businessContextAnalysis.domainAlignment || 'fair').toUpperCase()}
              </Badge>
              <svg
                className={`w-5 h-5 text-gray-500 transition-transform ${
                  expandedSections.has('business') ? 'transform rotate-180' : ''
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>

        {expandedSections.has('business') && (
          <div className="border-t p-4 space-y-4">
            {evaluation.businessContextAnalysis.findings && evaluation.businessContextAnalysis.findings.length > 0 && (
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Findings</h4>
                <ul className="space-y-1">
                  {evaluation.businessContextAnalysis.findings.map((finding, index) => (
                    <li key={index} className="text-sm text-gray-700 flex items-start">
                      <span className="text-blue-500 mr-2">•</span>
                      {finding}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {evaluation.businessContextAnalysis.missingDomainConcepts && evaluation.businessContextAnalysis.missingDomainConcepts.length > 0 && (
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Missing Domain Concepts</h4>
                <div className="flex flex-wrap gap-2">
                  {evaluation.businessContextAnalysis.missingDomainConcepts.map((concept, index) => (
                    <Badge key={index} className="bg-orange-100 text-orange-800 border-orange-200">
                      {concept}
                    </Badge>
                  ))}
                </div>
              </div>
            )}

            {evaluation.businessContextAnalysis.recommendations && evaluation.businessContextAnalysis.recommendations.length > 0 && (
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Recommendations</h4>
                <ul className="space-y-1">
                  {evaluation.businessContextAnalysis.recommendations.map((rec, index) => (
                    <li key={index} className="text-sm text-gray-700 flex items-start">
                      <span className="text-green-500 mr-2">→</span>
                      {rec}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </Card>

      {/* Technical Analysis */}
      <Card>
        <div
          className="p-4 cursor-pointer hover:bg-gray-50 transition-colors"
          onClick={() => toggleSection('technical')}
        >
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">
              Technical Analysis
            </h3>
            <div className="flex items-center space-x-4">
              <div className="text-sm text-gray-600">
                <span className="font-medium">{formatCoverage(evaluation.technicalAnalysis.indexCoverage)}</span> index coverage
                {' • '}
                <span className="font-medium">{formatCoverage(evaluation.technicalAnalysis.constraintCoverage)}</span> constraints
              </div>
              <svg
                className={`w-5 h-5 text-gray-500 transition-transform ${
                  expandedSections.has('technical') ? 'transform rotate-180' : ''
                }`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
              </svg>
            </div>
          </div>
        </div>

        {expandedSections.has('technical') && (
          <div className="border-t p-4 space-y-4">
            <div className="grid grid-cols-3 gap-4">
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <div className="text-xl font-bold text-gray-900">
                  {formatCoverage(evaluation.technicalAnalysis.indexCoverage)}
                </div>
                <div className="text-xs text-gray-600">Index Coverage</div>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <div className="text-xl font-bold text-gray-900">
                  {formatCoverage(evaluation.technicalAnalysis.constraintCoverage)}
                </div>
                <div className="text-xs text-gray-600">Constraint Coverage</div>
              </div>
              <div className="text-center p-3 bg-gray-50 rounded-lg">
                <Badge className={getAlignmentBadgeColor(evaluation.technicalAnalysis.relationshipQuality || 'good')}>
                  {(evaluation.technicalAnalysis.relationshipQuality || 'good').toUpperCase()}
                </Badge>
                <div className="text-xs text-gray-600 mt-1">Relationship Quality</div>
              </div>
            </div>

            {evaluation.technicalAnalysis.findings && evaluation.technicalAnalysis.findings.length > 0 && (
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Findings</h4>
                <ul className="space-y-1">
                  {evaluation.technicalAnalysis.findings.map((finding, index) => (
                    <li key={index} className="text-sm text-gray-700 flex items-start">
                      <span className="text-blue-500 mr-2">•</span>
                      {finding}
                    </li>
                  ))}
                </ul>
              </div>
            )}

            {evaluation.technicalAnalysis.recommendations && evaluation.technicalAnalysis.recommendations.length > 0 && (
              <div>
                <h4 className="font-medium text-gray-900 mb-2">Recommendations</h4>
                <ul className="space-y-1">
                  {evaluation.technicalAnalysis.recommendations.map((rec, index) => (
                    <li key={index} className="text-sm text-gray-700 flex items-start">
                      <span className="text-green-500 mr-2">→</span>
                      {rec}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        )}
      </Card>
    </div>
  );
};

// Sub-component for displaying individual executive question findings
const ExecutiveQuestionCard: React.FC<{ finding: ExecutiveQuestionFinding }> = ({ finding }) => {
  const [expanded, setExpanded] = useState(false);

  const getSupportBadgeColor = (supported: string): string => {
    switch (supported) {
      case 'full':
        return 'bg-green-100 text-green-800 border-green-200';
      case 'partial':
        return 'bg-yellow-100 text-yellow-800 border-yellow-200';
      case 'none':
        return 'bg-red-100 text-red-800 border-red-200';
      default:
        return 'bg-gray-100 text-gray-800 border-gray-200';
    }
  };

  return (
    <div className="border rounded-lg overflow-hidden">
      <div
        className="p-3 cursor-pointer hover:bg-gray-50 transition-colors"
        onClick={() => setExpanded(!expanded)}
      >
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center space-x-2 mb-1">
              <Badge className="text-xs">{finding.stakeholder}</Badge>
              <Badge className={`text-xs ${getSupportBadgeColor(finding.supported)}`}>
                {finding.supported === 'full' ? 'FULLY SUPPORTED' :
                 finding.supported === 'partial' ? 'PARTIALLY SUPPORTED' :
                 'NOT SUPPORTED'}
              </Badge>
              {finding.queryComplexity && (
                <span className="text-xs text-gray-500">
                  ({finding.queryComplexity} query)
                </span>
              )}
            </div>
            <div className="text-sm font-medium text-gray-900">{finding.question}</div>
          </div>
          <svg
            className={`w-5 h-5 text-gray-500 transition-transform flex-shrink-0 ml-2 ${
              expanded ? 'transform rotate-180' : ''
            }`}
            fill="none"
            stroke="currentColor"
            viewBox="0 0 24 24"
          >
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
          </svg>
        </div>
      </div>

      {expanded && (
        <div className="border-t bg-gray-50 p-3 space-y-3">
          <div>
            <h5 className="text-xs font-medium text-gray-700 mb-1">Reasoning</h5>
            <p className="text-sm text-gray-600">{finding.reasoning}</p>
          </div>

          {finding.missingElements && finding.missingElements.length > 0 && (
            <div>
              <h5 className="text-xs font-medium text-gray-700 mb-1">Missing Elements</h5>
              <div className="flex flex-wrap gap-1">
                {finding.missingElements.map((element, index) => (
                  <Badge key={index} className="text-xs bg-red-100 text-red-800 border-red-200">
                    {element}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {finding.requiredNodes && finding.requiredNodes.length > 0 && (
            <div>
              <h5 className="text-xs font-medium text-gray-700 mb-1">Required Nodes</h5>
              <div className="flex flex-wrap gap-1">
                {finding.requiredNodes.map((node, index) => (
                  <Badge key={index} className="text-xs bg-blue-100 text-blue-800 border-blue-200">
                    {node}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {finding.requiredRelationships && finding.requiredRelationships.length > 0 && (
            <div>
              <h5 className="text-xs font-medium text-gray-700 mb-1">Required Relationships</h5>
              <div className="flex flex-wrap gap-1">
                {finding.requiredRelationships.map((rel, index) => (
                  <Badge key={index} className="text-xs bg-purple-100 text-purple-800 border-purple-200">
                    {rel}
                  </Badge>
                ))}
              </div>
            </div>
          )}

          {finding.recommendations && finding.recommendations.length > 0 && (
            <div>
              <h5 className="text-xs font-medium text-gray-700 mb-1">Recommendations</h5>
              <ul className="space-y-1">
                {finding.recommendations.map((rec, index) => (
                  <li key={index} className="text-sm text-gray-600 flex items-start">
                    <span className="text-green-500 mr-1">→</span>
                    {rec}
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
};
